import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { API_BASE_URL, getAuthHeaders, getStoredUser } from '../auth';
import { LoadingPane, PageHeader } from '../components/page-elements';

const LIABILITY_ACKNOWLEDGMENT = 'LIABILITY ACKNOWLEDGMENT: BY SIGNING ABOVE AND/OR ACCEPTING POSSESSION OF THE LOAD, DRIVER AND CARRIER ACKNOWLEDGE THAT THE MATERIALS HAVE BEEN LOADED BY THE RAILROAD IN APPARENT GOOD ORDER AND CONDITION, UNLESS OTHERWISE NOTED AT THE TIME OF LOADING. FROM THAT MOMENT FORWARD, DRIVER AND CARRIER ASSUME FULL AND EXCLUSIVE RESPONSIBILITY AND LIABILITY FOR THE CUSTODY, SECUREMENT, TRANSPORT, AND DELIVERY OF THE MATERIALS. THE RAILROAD SHALL HAVE NO LIABILITY FOR ANY LOSS, DAMAGE, CONTAMINATION, SPILLAGE, DELAY, OR CLAIM OF ANY KIND ARISING AFTER DRIVER ACCEPTS THE LOAD, REGARDLESS OF CAUSE. CARRIER AGREES TO INDEMNIFY, DEFEND, AND HOLD HARMLESS THE RAILROAD, ITS AFFILIATES, AND EMPLOYEES FROM ANY AND ALL SUCH CLAIMS, DAMAGES, OR EXPENSES.';

const toSafeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const CompleteBOL = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useMemo(() => getStoredUser(), []);
  const isInternal = user?.role === 'internal' || user?.role === 'admin';

  const [bol, setBol] = useState(null);
  const [formData, setFormData] = useState({
    grossWeight: '',
    tareWeight: '',
    splitLoad: false,
    secondaryRailcarID: '',
    secondaryGrossWeight: '',
    secondaryTareWeight: '',
    weighInTime: new Date(),
    weighOutTime: new Date(),
    driverName: '',
    driverSignatureImage: '',
    comments: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [secondaryRailcarOptions, setSecondaryRailcarOptions] = useState([]);
  const [secondaryRailcarLookupLoading, setSecondaryRailcarLookupLoading] = useState(false);
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const inventorySource = bol?.inventorySource === 'ground' ? 'ground' : 'railcar';

  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    ctx.clearRect(0, 0, rect.width, rect.height);
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/bols/${id}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load BOL');
        return res.json();
      })
      .then((data) => {
        setBol(data);
        setFormData((prev) => ({
          ...prev,
          grossWeight: data.grossWeight ?? '',
          tareWeight: data.tareWeight ?? '',
          splitLoad: Boolean(data.splitLoad),
          secondaryRailcarID: data.secondaryRailcarID || '',
          secondaryGrossWeight: data.secondaryGrossWeight ?? '',
          secondaryTareWeight: data.secondaryTareWeight ?? data.grossWeight ?? '',
          weighInTime: data.weighInTime
            ? new Date(data.weighInTime)
            : (data.bolDate
              ? new Date(data.bolDate)
              : (data.createdAt ? new Date(data.createdAt) : prev.weighInTime)),
          weighOutTime: data.weighOutTime ? new Date(data.weighOutTime) : prev.weighOutTime,
          comments: data.comments || '',
          driverName: data.driverName || '',
          driverSignatureImage: data.driverSignatureImage || '',
        }));
      })
      .catch((err) => setError(err.message));
  }, [id]);

  useEffect(() => {
    setupCanvas();
  }, [bol]);

  useEffect(() => {
    if (!bol) return;

    const customerId =
      bol.customerName?._id ||
      bol.customerName ||
      bol.orderNumber?.customerName?._id ||
      bol.orderNumber?.customerName;

    if (!customerId) return;

    const params = new URLSearchParams({
      customerId: String(customerId),
    });

    setSecondaryRailcarLookupLoading(true);
    fetch(`${API_BASE_URL}/api/railcars?${params.toString()}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load customer railcars');
        return res.json();
      })
      .then((rows) => {
        const primaryRailcarId = String(bol.railcarID || '').trim();
        const options = [...new Set(
          (rows || [])
            .map((railcar) => String(railcar.railcarID || '').trim())
            .filter((railcarId) => railcarId && railcarId !== primaryRailcarId)
        )].sort();
        setSecondaryRailcarOptions(options);
      })
      .catch(() => setSecondaryRailcarOptions([]))
      .finally(() => setSecondaryRailcarLookupLoading(false));
  }, [bol]);

  useEffect(() => {
    const onResize = () => setupCanvas();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!formData.driverSignatureImage || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
      ctx.drawImage(img, 0, 0, rect.width, rect.height);
    };
    img.src = formData.driverSignatureImage;
  }, [formData.driverSignatureImage]);

  if (!isInternal) {
    return <Typography>Only internal users can complete BOLs.</Typography>;
  }

  const primaryGross = toSafeNumber(formData.grossWeight);
  const primaryTare = toSafeNumber(formData.tareWeight);
  const primaryNetWeight = primaryGross - primaryTare;
  const primaryTonWeight = primaryNetWeight / 2000;

  const secondaryGross = toSafeNumber(formData.secondaryGrossWeight);
  const secondaryTare = toSafeNumber(formData.splitLoad ? formData.grossWeight : formData.secondaryTareWeight);
  const secondaryNetWeight = secondaryGross - secondaryTare;
  const secondaryTonWeight = secondaryNetWeight / 2000;

  const totalNetWeight = primaryNetWeight + (formData.splitLoad ? secondaryNetWeight : 0);
  const totalTonWeight = totalNetWeight / 2000;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, date) => {
    setFormData((prev) => ({ ...prev, [name]: date }));
  };

  const handleSplitToggle = (event) => {
    const enabled = event.target.checked;
    setFormData((prev) => ({
      ...prev,
      splitLoad: enabled,
      secondaryRailcarID: enabled ? prev.secondaryRailcarID : '',
      secondaryGrossWeight: enabled ? prev.secondaryGrossWeight : '',
      secondaryTareWeight: enabled ? String(prev.grossWeight ?? '') : '',
    }));
  };

  const handleSaveDraft = async () => {
    if (!bol?._id) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/bols/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          grossWeight: formData.grossWeight === '' ? null : Number(formData.grossWeight),
          tareWeight: formData.tareWeight === '' ? null : Number(formData.tareWeight),
          splitLoad: formData.splitLoad,
          secondaryRailcarID: formData.splitLoad ? formData.secondaryRailcarID.trim() : '',
          secondaryGrossWeight: formData.splitLoad && formData.secondaryGrossWeight !== '' ? Number(formData.secondaryGrossWeight) : null,
          secondaryTareWeight: formData.splitLoad && formData.grossWeight !== '' ? Number(formData.grossWeight) : null,
          weighInTime: formData.weighInTime ? formData.weighInTime.toISOString() : null,
          weighOutTime: formData.weighOutTime ? formData.weighOutTime.toISOString() : null,
          driverName: formData.driverName?.trim() || '',
          driverSignatureImage: formData.driverSignatureImage || '',
          comments: formData.comments,
          status: 'Draft',
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || 'Failed to save draft');
      }

      setSuccess('Draft saved.');
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  const getPointerPosition = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const startDrawing = (event) => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPointerPosition(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
    isDrawingRef.current = true;
  };

  const draw = (event) => {
    if (!isDrawingRef.current || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPointerPosition(event);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDrawing = () => {
    if (!canvasRef.current) return;
    if (isDrawingRef.current) {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      setFormData((prev) => ({ ...prev, driverSignatureImage: dataUrl }));
    }
    isDrawingRef.current = false;
  };

  const clearSignature = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    setFormData((prev) => ({ ...prev, driverSignatureImage: '' }));
  };

  const isNumberFieldValid = (value) => value !== '' && Number.isFinite(Number(value));

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    if (!isNumberFieldValid(formData.grossWeight)) {
      setError('Primary gross weight is required and must be numeric.');
      setLoading(false);
      return;
    }

    if (!isNumberFieldValid(formData.tareWeight)) {
      setError('Primary tare weight is required and must be numeric.');
      setLoading(false);
      return;
    }

    if (Number(formData.grossWeight) < 0 || Number(formData.tareWeight) < 0) {
      setError('Primary gross and tare weights cannot be negative.');
      setLoading(false);
      return;
    }

    if (Number(formData.grossWeight) < Number(formData.tareWeight)) {
      setError('Primary gross weight must be greater than or equal to primary tare weight.');
      setLoading(false);
      return;
    }

    if (!formData.weighInTime || !formData.weighOutTime) {
      setError('Weigh in and weigh out times are required.');
      setLoading(false);
      return;
    }

    if (!formData.driverName.trim()) {
      setError('Driver name is required.');
      setLoading(false);
      return;
    }

    if (!formData.driverSignatureImage) {
      setError('Driver signature is required.');
      setLoading(false);
      return;
    }

    if (inventorySource !== 'ground' && formData.splitLoad) {
      if (!formData.secondaryRailcarID.trim()) {
        setError('Secondary railcar ID is required for split loads.');
        setLoading(false);
        return;
      }

      if (formData.secondaryRailcarID.trim() === String(bol.railcarID || '').trim()) {
        setError('Secondary railcar ID must be different from the primary railcar ID.');
        setLoading(false);
        return;
      }

      if (!isNumberFieldValid(formData.secondaryGrossWeight)) {
        setError('Secondary gross weight is required and must be numeric for split loads.');
        setLoading(false);
        return;
      }

      if (Number(formData.secondaryGrossWeight) < 0) {
        setError('Secondary gross weight cannot be negative.');
        setLoading(false);
        return;
      }

      if (Number(formData.secondaryGrossWeight) < Number(formData.grossWeight)) {
        setError('Secondary gross weight must be greater than or equal to primary gross weight.');
        setLoading(false);
        return;
      }
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/bols/${id}/complete`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          grossWeight: Number(formData.grossWeight),
          tareWeight: Number(formData.tareWeight),
          inventorySource,
          groundInventoryLot: inventorySource === 'ground' ? bol.groundInventoryLot?._id || bol.groundInventoryLot || null : null,
          secondaryGroundInventoryLot:
            inventorySource === 'ground' && formData.splitLoad
              ? bol.secondaryGroundInventoryLot?._id || bol.secondaryGroundInventoryLot || null
              : null,
          splitLoad: formData.splitLoad,
          secondaryRailcarID: inventorySource === 'ground' ? '' : formData.splitLoad ? formData.secondaryRailcarID.trim() : '',
          secondaryGrossWeight: formData.splitLoad ? Number(formData.secondaryGrossWeight) : null,
          secondaryTareWeight: formData.splitLoad ? Number(formData.grossWeight) : null,
          weighInTime: formData.weighInTime.toISOString(),
          weighOutTime: formData.weighOutTime.toISOString(),
          driverName: formData.driverName.trim(),
          driverSignatureImage: formData.driverSignatureImage,
          signedAt: new Date().toISOString(),
          comments: formData.comments,
          completedBy: user._id,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || err?.errors?.[0]?.msg || 'Failed to complete BOL');
      }

      if (bol?.orderNumber?._id) {
        await fetch(`${API_BASE_URL}/api/orders/${bol.orderNumber._id}`, {
          method: 'PUT',
          headers: getAuthHeaders(true),
          body: JSON.stringify({ orderStatus: 'Shipped' }),
        });
      }

      setSuccess('BOL completed successfully.');
      setTimeout(() => navigate('/bols'), 700);
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  if (!bol) {
    if (error) {
      return (
        <Box p={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      );
    }
    return <LoadingPane label="Loading BOL..." />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={2}>
        <PageHeader
          title="Complete BOL"
          subtitle="Capture final weights, timestamps, and driver signature before completion."
          actions={[
            <Button key="back" variant="outlined" onClick={() => navigate('/bols')}>
              Back to BOL Queue
            </Button>,
          ]}
        />

        {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
        {success ? <Alert severity="success" sx={{ mb: 1.5 }}>{success}</Alert> : null}

        <Grid container spacing={1.5}>
          <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
            <Card variant="outlined" sx={{ borderRadius: 2, width: '100%', display: 'flex' }}>
              <CardContent sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.8 }}>
                  Draft Information
                </Typography>
                <Stack spacing={0.6}>
                  <Typography variant="body2"><strong>Order Number:</strong> {bol.orderNumber?.orderNumber || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Primary Railcar:</strong> {bol.railcarID || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Inventory Source:</strong> {inventorySource === 'ground' ? 'Ground Inventory' : 'Railcar'}</Typography>
                  {inventorySource === 'ground' ? (
                    <Typography variant="body2"><strong>Ground Lot:</strong> {bol.groundInventoryLot?._id || bol.groundInventoryLot || 'N/A'}</Typography>
                  ) : null}
                  <Typography variant="body2"><strong>Truck ID:</strong> {bol.truckID || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Trailer ID:</strong> {bol.trailerID || 'N/A'}</Typography>
                </Stack>
                <Divider sx={{ my: 1.2 }} />
                <Stack spacing={0.55}>
                  <Typography variant="body2"><strong>Customer:</strong> {bol.orderNumber?.customerName?.customerName || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Receiver:</strong> {bol.orderNumber?.receiverName?.receiverName || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Location:</strong> {bol.orderNumber?.projectName?.projectName || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Material:</strong> {bol.orderNumber?.materialName?.materialName || 'N/A'}</Typography>
                </Stack>
                <Divider sx={{ my: 1.2 }} />
                <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                  <Chip label={formData.splitLoad ? 'Split Load' : 'Single Load'} size="small" color={formData.splitLoad ? 'warning' : 'default'} />
                  <Chip label={`Primary Net ${primaryNetWeight || 0}`} size="small" color="info" variant="outlined" />
                  <Chip label={`Total Net ${totalNetWeight || 0}`} size="small" color="success" />
                  <Chip label={`Total Tons ${Number(totalTonWeight || 0).toFixed(2)}`} size="small" color="primary" variant="outlined" />
                  {inventorySource === 'ground' && formData.splitLoad ? (
                    <Chip
                      label={`Secondary Lot ${bol.secondaryGroundInventoryLot?._id || bol.secondaryGroundInventoryLot || 'N/A'}`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  ) : null}
                </Stack>
                <Box mt="auto" pt={1.2}>
                  <Typography variant="caption" color="text.secondary">
                    Review draft details before entering final weights and signature.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={8} sx={{ display: 'flex' }}>
            <Card variant="outlined" sx={{ borderRadius: 2, width: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Load Weights
                </Typography>
                <Grid container spacing={1.2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Primary Gross Weight"
                      name="grossWeight"
                      value={formData.grossWeight}
                      onChange={handleChange}
                      fullWidth
                      margin="normal"
                      type="number"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Primary Tare Weight"
                      name="tareWeight"
                      value={formData.tareWeight}
                      onChange={handleChange}
                      fullWidth
                      margin="normal"
                      type="number"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Primary Net Weight" value={primaryNetWeight} fullWidth margin="normal" disabled />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Primary Ton Weight" value={primaryTonWeight} fullWidth margin="normal" disabled />
                  </Grid>
                </Grid>

                <Box mt={1}>
                  <FormControlLabel
                    control={<Switch checked={formData.splitLoad} onChange={handleSplitToggle} color="primary" />}
                    label={inventorySource === 'ground' ? 'Split load (multiple pull sequence)' : 'Split load (unloaded from two railcars)'}
                  />
                </Box>

                {formData.splitLoad ? (
                  <>
                    <Divider sx={{ my: 1.2 }} />
                    <Typography variant="subtitle2" color="text.secondary">Secondary Load</Typography>
                    <Grid container spacing={1.2}>
                      {inventorySource !== 'ground' ? (
                        <Grid item xs={12}>
                          <Autocomplete
                            freeSolo
                            options={secondaryRailcarOptions}
                            value={formData.secondaryRailcarID}
                            onInputChange={(_, value) =>
                              setFormData((prev) => ({ ...prev, secondaryRailcarID: value || '' }))
                            }
                            onChange={(_, value) =>
                              setFormData((prev) => ({ ...prev, secondaryRailcarID: value || '' }))
                            }
                            loading={secondaryRailcarLookupLoading}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Secondary Railcar ID"
                                margin="normal"
                                fullWidth
                                required
                                helperText="Lookup includes all railcars for this customer."
                              />
                            )}
                          />
                        </Grid>
                      ) : null}
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Secondary Gross Weight"
                          name="secondaryGrossWeight"
                          value={formData.secondaryGrossWeight}
                          onChange={handleChange}
                          fullWidth
                          margin="normal"
                          type="number"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Secondary Tare Weight"
                          name="secondaryTareWeight"
                          value={formData.grossWeight}
                          fullWidth
                          margin="normal"
                          type="number"
                          disabled
                          helperText="Auto-set to Primary Gross Weight"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="Secondary Net Weight" value={secondaryNetWeight} fullWidth margin="normal" disabled />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="Secondary Ton Weight" value={secondaryTonWeight} fullWidth margin="normal" disabled />
                      </Grid>
                    </Grid>
                  </>
                ) : null}

                <Divider sx={{ my: 1.2 }} />
                <Grid container spacing={1.2}>
                  <Grid item xs={12} md={6}>
                    <TextField label="Total Net Weight" value={totalNetWeight} fullWidth margin="normal" disabled />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Total Ton Weight" value={totalTonWeight} fullWidth margin="normal" disabled />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Times & Driver
                </Typography>
                <DateTimePicker
                  label="Weigh In Time"
                  value={formData.weighInTime}
                  onChange={(date) => handleDateChange('weighInTime', date)}
                  renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                />
                <DateTimePicker
                  label="Weigh Out Time"
                  value={formData.weighOutTime}
                  onChange={(date) => handleDateChange('weighOutTime', date)}
                  renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                />
                <TextField
                  label="Driver Name"
                  name="driverName"
                  value={formData.driverName}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  required
                />
                <TextField
                  label="Comments"
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={3}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Driver Signature
                </Typography>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Box
                    ref={canvasRef}
                    component="canvas"
                    sx={{
                      width: '100%',
                      height: 200,
                      border: '1px dashed #9ca3af',
                      borderRadius: 1,
                      touchAction: 'none',
                      backgroundColor: '#fff',
                      cursor: 'crosshair',
                    }}
                    onPointerDown={startDrawing}
                    onPointerMove={draw}
                    onPointerUp={endDrawing}
                    onPointerLeave={endDrawing}
                  />
                  <Box mt={1}>
                    <Button variant="outlined" size="small" onClick={clearSignature}>
                      Clear Signature
                    </Button>
                  </Box>
                </Paper>
                <Box mt={1.2} p={1.5} sx={{ border: '1px solid #d1d5db', borderRadius: 1, backgroundColor: '#f9fafb' }}>
                  <Typography variant="caption" color="text.secondary">
                    {LIABILITY_ACKNOWLEDGMENT}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={1} mt={2} justifyContent="flex-end">
          <Button color="warning" variant="outlined" onClick={handleSaveDraft} disabled={loading}>
            {loading ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button color="success" variant="contained" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving...' : 'Complete BOL'}
          </Button>
        </Stack>
      </Box>
    </LocalizationProvider>
  );
};

export default CompleteBOL;
